import React from 'react';
import { Box, Typography, Tooltip } from "@mui/material";
import { styled } from '@mui/material/styles';
import { tooltipClasses } from '@mui/material/Tooltip';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { colors } from '../../utils/themeUtils';

// Custom styled tooltip to match the app's tooltip style
const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 'none',
    whiteSpace: 'nowrap',
    backgroundColor: 'rgba(250, 250, 252, 0.98)',
    padding: '10px 14px',
    fontSize: '0.85rem',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    color: '#333',
    '&.MuiTooltip-tooltipPlacementTop': {
      marginBottom: '6px'
    }
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: 'rgba(250, 250, 252, 0.98)'
  }
});

/**
 * Component to render a single subject action item in the Recommended Actions section
 */
const SubjectActionItem = ({
  subject,
  attributes,
  view,
  isSubjectHighlighted,
  isAttributeHighlightingParent,
  handleSubjectClick,
  handleAttributeClick,
  formatCurrency,
  formatImpact,
  capitalizeSubject,
  capitalizeAttribute,
  getSubjectImpactAction
}) => {
  // Helper function to get impact text with colored styling
  const getImpactElement = (isPositive, impact, revenueImpact) => {
    const value = view === "Sales" ? revenueImpact : impact;
    const formattedValue = view === "Sales" 
      ? formatCurrency(Math.abs(value || 0))
      : formatImpact(Math.abs(value || 0));
    
    return (
      <span style={{ 
        color: isPositive ? colors.positiveGreen : colors.negativeRed,
        fontWeight: 600
      }}>
        {isPositive ? '+' : '-'}{formattedValue}
      </span>
    );
  };

  return (
    <Box 
      data-action-box="true"
      data-filter-exempt="true"
      sx={{ 
        padding: '0rem 1rem',
        mb: 0.5,
        display: 'flex',
        width: '100%',
        borderRadius: 1,
        cursor: 'pointer',
        backgroundColor: 'transparent', // Always transparent background
        '&:hover .subject-name': {
          textDecoration: 'underline' // Underline subject name when parent is hovered
        }
      }}
      onClick={(e) => {
        // Only trigger subject click if the click was directly on the box
        // or on a non-clickable element (not an attribute)
        if (
          e.target.getAttribute('data-action-box') === 'true' ||
          (!e.target.getAttribute('data-filter-term') &&
           !e.target.closest('[data-filter-term="attribute"]'))
        ) {
          // Apply same functionality as clicking on the subject directly
          // This will toggle the subject selection without affecting category selection
          e.stopPropagation(); // Prevent any parent handlers
          handleSubjectClick(subject.name);
        }
      }}
    >
      {/* Icon */}
      <Box sx={{ mr: 1, mt: 1, flexShrink: 0 }} data-filter-exempt="true">
        {subject.isPositive ? (
          <CheckCircleOutlineIcon sx={{ color: colors.positiveGreen, fontSize: '1.25rem' }} data-filter-exempt="true" />
        ) : (
          <ErrorOutlineIcon sx={{ color: colors.negativeRed, fontSize: '1.25rem' }} data-filter-exempt="true" />
        )}
      </Box>
      
      {/* Text content */}
      <Box sx={{ flex: 1, py: 0.5, pr: 0.5 }} data-filter-exempt="true">
        <Typography component="div" variant="body1" sx={{ fontSize: '1.125rem', lineHeight: 1.7 }} data-filter-exempt="true">
          {getSubjectImpactAction(subject.isPositive)}
          
          {/* Subject and Impact */}
          <StyledTooltip 
            title={getImpactElement(subject.isPositive, subject.impact, subject.revenueImpact)}
            arrow
            placement="top"
          >
            <Box
              component="span" 
              className="subject-name"
              data-filter-term="subject"
              data-filter-exempt="true"
              sx={{
                fontWeight: 'bold',
                color: subject.isPositive ? colors.positiveGreen : colors.negativeRed,
                display: 'inline',
                position: 'relative',
                whiteSpace: 'nowrap',
                textDecoration: isSubjectHighlighted(subject.name) ? 'underline' : 'none'
              }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent triggering the box's onClick
                handleSubjectClick(subject.name);
              }}
            >
              {capitalizeSubject(subject.name)}
            </Box>
          </StyledTooltip>
          
          {/* Add space between subject and attributes */}
          {attributes.length > 0 && (
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 'bold' }} data-filter-exempt="true">: </Box>
          )}
          
          {/* Attributes - individually clickable */}
          {attributes.length > 0 && attributes.map((attr, i) => (
            <React.Fragment key={i} data-filter-exempt="true">
              <StyledTooltip 
                title={getImpactElement(attr.sentiment === 'positive', attr.impact, attr.revenueImpact)}
                arrow
                placement="top"
              >
                <Box
                  component="span"
                  data-filter-term="attribute"
                  data-filter-exempt="true"
                  sx={{
                    color: attr.sentiment === 'positive' ? colors.positiveGreen : colors.negativeRed,
                    cursor: 'pointer',
                    display: 'inline',
                    position: 'relative',
                    whiteSpace: 'nowrap',
                    textDecoration: isAttributeHighlightingParent(subject.name, attr.name) ? 'underline' : 'none',
                    '&:hover': {
                      textDecoration: 'underline' // Keep attribute hover underline
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering the box's onClick
                    handleAttributeClick(attr.name, subject.name);
                  }}
                >
                  {capitalizeAttribute(attr.name)}
                </Box>
              </StyledTooltip>
              {i < attributes.length - 1 && (
                <Box component="span" data-filter-exempt="true">, </Box>
              )}
            </React.Fragment>
          ))}
        </Typography>
      </Box>
    </Box>
  );
};

export default SubjectActionItem; 